/**
 * "Gửi hook cho khách": on a staff write that changes an object owned by a customer, staff
 * choose whether the customer's webhook endpoints are told (default: yes). Platform endpoints
 * always receive the event (docs/integrations/webhooks.md).
 *
 * - `CustomerScope` (page level): the customer owning the object the page shows.
 * - `NotifyCustomerChoice` (dialog level): one choice per dialog; `ActionDialog` and the domain
 *   action dialog provide it.
 * - `NotifyCustomerField`: the checkbox, shown only inside a dialog about a customer's object
 *   (reason fields render it).
 * - `useWrite` sends the choice as `notify_customer` (`WriteOptions.notifyCustomer`).
 */

import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@/components/ui/checkbox';

const ScopeCtx = createContext<{ customerId: string | null } | null>(null);

interface Choice {
  shown: boolean;
  notify: boolean;
  setNotify: (v: boolean) => void;
}

const ChoiceCtx = createContext<Choice | null>(null);

export function CustomerScope({ customerId, children }: { customerId: string | null | undefined; children: ReactNode }) {
  const value = useMemo(() => ({ customerId: customerId ?? null }), [customerId]);
  return <ScopeCtx.Provider value={value}>{children}</ScopeCtx.Provider>;
}

export function NotifyCustomerChoice({ children }: { children: ReactNode }) {
  const scope = useContext(ScopeCtx);
  const [notify, setNotify] = useState(true);
  const value = useMemo(() => ({ shown: !!scope?.customerId, notify, setNotify }), [scope?.customerId, notify]);
  return <ChoiceCtx.Provider value={value}>{children}</ChoiceCtx.Provider>;
}

/** `false` when staff unticked the box in this dialog; `undefined` when there is no choice. */
export function useNotifyCustomer(): boolean | undefined {
  const c = useContext(ChoiceCtx);
  return c?.shown ? c.notify : undefined;
}

export function NotifyCustomerField() {
  const { t } = useTranslation();
  const c = useContext(ChoiceCtx);
  if (!c?.shown) return null;
  return (
    // eslint-disable-next-line jsx-a11y/label-has-associated-control -- the checkbox is the child
    <label className="flex items-start gap-2 text-sm">
      <Checkbox checked={c.notify} onCheckedChange={(v) => c.setNotify(v === true)} className="mt-0.5" aria-describedby="notify-customer-hint" />
      <span className="grid gap-0.5">
        <span className="font-medium">{t('write.notifyCustomer')}</span>
        <span id="notify-customer-hint" className="text-xs text-muted-foreground">
          {t('write.notifyCustomerHint')}
        </span>
      </span>
    </label>
  );
}
